import { TOAST_DEFAULT_OPTIONS } from '@/constants/toast';
import useAnalyticsCalls from '@/hooks/use-analytics';
import useOnMount from '@/hooks/useOnMount';
import { useSetIntercomIsVisible } from '@/queries/layout.queries';
import { useToast } from '@cmpsr/components';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Questionnaire from '../questionnaire/components/questionnaire/questionnaire';
import { usePpraController } from './ppra.controller';
import { Status } from './types';

export default function PpraPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const setIntercomIsVisible = useSetIntercomIsVisible();
  const { status, error, userQuestionnaire, decisionTreeCallback, backOnFirstSurveyItem, onItemViewed } =
    usePpraController();

  // Hide intercom when the component mounts
  useOnMount(() => {
    setIntercomIsVisible.mutate(false);
    return () => {
      setIntercomIsVisible.mutate(true);
    };
  });

  useEffect(() => {
    if (error) {
      toast({
        ...TOAST_DEFAULT_OPTIONS,
        title: 'Error',
        description: 'Error connecting to the Catch server.',
        status: 'error',
      });
    }
  }, [error, toast]);

  const { callPpraModalViewed } = useAnalyticsCalls();
  const hasTrackedModalView = useRef(false);

  // Track the modal view if it hasn't been tracked yet
  useEffect(() => {
    if (!hasTrackedModalView.current && userQuestionnaire) {
      callPpraModalViewed(userQuestionnaire.nextSurveyItem.surveyItemId);
      hasTrackedModalView.current = true;
    }
  }, [userQuestionnaire, callPpraModalViewed]);

  // Navigate to the results loading screen if the survey is completed
  useEffect(() => {
    if (status === Status.completed && userQuestionnaire) {
      // Navigate to the results loading screen as a separate route
      navigate(`/free-assessment/results-loading/${userQuestionnaire.id}`);
      return;
    }
  }, [status, userQuestionnaire, navigate]);

  if (!userQuestionnaire) {
    return null;
  }

  return (
    <Questionnaire
      userQuestionnaire={userQuestionnaire}
      decisionTreeCallback={decisionTreeCallback}
      backOnFirstSurveyItem={backOnFirstSurveyItem}
      onItemViewed={onItemViewed}
    />
  );
}
